<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface DataTableInstance {
  loadData: () => void;
}

const { t } = useI18n();
const { initialized } = useFiltro();
const dataTableRef = ref<DataTableInstance | null>(null);
const dataLoaded = ref<boolean>(false);

const endpoint = computed(() => '/index.php/relatorios/servicos_realizados/lista_servicos_realizados');

const tableColumns = computed(() => [
  {
    data: 'franquia',
    title: t('Franquia'),
    width: 150,
    sortable: true
  },
  {
    data: 'operador',
    title: t('Operador'),
    width: 150,
    sortable: true
  },
  {
    data: 'atendente',
    title: t('Vendedor'),
    width: 150,
    sortable: true
  },
  {
    data: 'cliente_nome',
    title: t('Cliente'),
    width: 200,
    sortable: true
  },
  {
    data: 'valor_no_compromisso',
    title: t('Valor no compromisso'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'valor_no_orcamento',
    title: t('Valor no orçamento'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'agendado_no',
    title: t('Origem'),
    width: 120,
    sortable: true
  },
  {
    data: 'tipo',
    title: t('Tipo'),
    width: 150,
    sortable: true
  },
  {
    data: 'nome',
    title: t('Serviço'),
    width: 200,
    sortable: true
  },
  {
    data: 'compromisso_id',
    title: t('Compromisso ID'),
    width: 120,
    sortable: true
  },
  {
    data: 'compromisso_titulo',
    title: t('Bairro'),
    width: 150,
    sortable: true
  },
  {
    data: 'compromisso_total',
    title: t('Total Compromisso'),
    width: 150,
    sortable: true,
    renderType: 'currency'
  },
  {
    // Exibe a data JA FORMATADA pelo idioma da pessoa (o modelo monta
    // `data_compromisso` com DATE_FORMAT). A coluna mostrava
    // `compromisso_data_hora_inicio` cru — "2026-09-18 15:30:00", formato de
    // banco, o unico lugar do sistema onde a data nao aparecia como dd/mm/aaaa
    // (chamado 2026-0011).
    //
    // A ordenacao continua no campo CRU: ordenar a string formatada colocaria
    // 01/12 antes de 02/01, porque seria ordem alfabetica e nao cronologica.
    data: 'data_compromisso',
    sortField: 'compromisso_data_hora_inicio',
    title: t('Data Compromisso'),
    width: 180,
    sortable: true
  }
]);

const dataTableConfig = computed(() => ({
  tableId: 'tabelaServicosRealizados',
  filename: `servicos_realizados_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 25,
  pagination: true,
  footerLabel: t('Totais'),
  rowSelection: 'single',
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small'
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
  refreshName: t('Atualizar'),
  printName: t('Imprimir'),
  debug: false,
  columns: {
    'franquia': 'text',
    'operador': 'select',
    'atendente': 'select',
    'cliente_nome': 'text',
    'valor_no_compromisso': 'range',
    'valor_no_orcamento': 'range',
    'agendado_no': 'select',
    'tipo': 'select',
    'nome': 'select',
    'compromisso_id': 'text',
    'compromisso_titulo': 'text',
    'compromisso_total': 'range',
    // Acompanha o `data` da coluna. Filtrar pelo valor EXIBIDO também é o que
    // a pessoa espera: ela digita "18/09", não "2026-09-18".
    'data_compromisso': 'text'
  }
}));

const csvButtonConfig = computed(() => ({ show: true, text: t('CSV'), loading: false }));
const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));

const loadData = () => {
  const stack = new Error().stack;
  log('ServicosRealizadosTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (dataTableRef.value) dataTableRef.value.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('ServicosRealizadosTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  dataLoaded.value = true;
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros de serviços realizados`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('ServicosRealizadosTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar serviços realizados: ') + message);
};

// Named handler for proper cleanup
const onFilterUpdated = (): void => {
  log('ServicosRealizadosTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

onMounted(() => {
  log('ServicosRealizadosTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('ServicosRealizadosTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('ServicosRealizadosTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

defineExpose({ loadData });
</script>
